/* import __COLOCATED_TEMPLATE__ from './rollout-percentage-selector.hbs'; */
/* RESPONSIBLE TEAM: team-phone */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type { PopoverAPI } from '@intercom/pulse/template-registry';

interface Args {
  rolloutPercentage: number;
  updateRolloutPercentage: (rolloutPercentage: number) => Promise<void>;
  disabled: boolean;
}

interface Signature {
  Element: HTMLElement;
  Args: Args;
}

export default class RolloutPercentageSelector extends Component<Signature> {
  @service declare intl: IntlService;

  @tracked _rolloutPercentage = this.args.rolloutPercentage;

  get rolloutPercentage() {
    return this._rolloutPercentage;
  }

  set rolloutPercentage(value: any) {
    let parsedValue = parseInt(value, 10);

    if (!parsedValue) {
      this._rolloutPercentage = 0;
    } else if (parsedValue > 100) {
      this._rolloutPercentage = 100;
    } else {
      this._rolloutPercentage = parsedValue;
    }
  }

  get savingDisabled() {
    return !this.isValid || this.args.disabled;
  }

  get isValid() {
    return this.rolloutPercentage >= 0 && this.rolloutPercentage <= 100;
  }

  get savedRolloutPercentage() {
    return this.args.rolloutPercentage || 0;
  }

  get buttonType() {
    return !this.savedRolloutPercentage ? 'primary' : 'primary-live';
  }

  get buttonLabel() {
    if (!this.savedRolloutPercentage) {
      return this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.rollout-percentage-selector.set-fin-live',
      );
    }

    if (this.savedRolloutPercentage === 100) {
      return this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.rollout-percentage-selector.live-to-all',
      );
    }

    return this.intl.t(
      'operator.fin.setup.tabs.setup-and-go-live.phone.rollout-percentage-selector.partially-live',
      {
        percentage: this.savedRolloutPercentage,
      },
    );
  }

  @action
  loadRolloutPercentage() {
    this._rolloutPercentage = this.savedRolloutPercentage;
  }

  @action
  async updateRolloutPercentage(popover: PopoverAPI) {
    if (!this.isValid) {
      return;
    }
    await this.args.updateRolloutPercentage(this.rolloutPercentage);
    popover.hide();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::Components::RolloutPercentageSelector': typeof RolloutPercentageSelector;
  }
}
